import React, { useState } from 'react';
import { FiCheck, FiCpu, FiFilm, FiMessageCircle, FiMic, FiStar, FiUser, FiZap } from 'react-icons/fi';
import { markOnboardingCompleted } from '../../utils/onboarding';

const cards = [
  {
    title: 'Welcome to Morphosys Screenwriting',
    body: ['Morphosys automatically formats your screenplay as you write.', 'Start your script with a Scene Heading such as:'],
    example: 'INT. OFFICE – DAY',
    after: ['Press Enter and Morphosys will move you to Action.'],
    icon: FiFilm,
  },
  {
    title: 'Action',
    body: ['In Action, describe what the audience sees on screen.', 'Example:'],
    example: 'Sarah walks into the office carrying a folder.',
    after: ['Press Enter and Morphosys will move you to Character.'],
    icon: FiFilm,
  },
  {
    title: 'Character',
    body: ['Enter the character’s name.', 'Example:'],
    example: 'SARAH',
    after: ['Press Enter and Morphosys will move you to Dialogue.'],
    icon: FiUser,
  },
  {
    title: 'Dialogue',
    body: ['Write what the character says.', 'Example:'],
    example: 'We need to talk about the meeting.',
    after: [
      'Press Enter and Morphosys will move to the next Character.',
      'To return to Action, Scene Heading, Parenthetical, Transition or another screenplay element, use the Element Menu.',
    ],
    icon: FiMessageCircle,
  },
  {
    title: 'Voice Overs & Special Dialogue',
    body: ['Enter the character’s name and press the ( key.', 'Morphosys will display options including:'],
    list: ['VO – Voice Over', 'OS – Off Screen', 'OC – Off Camera'],
    exampleLabel: 'Example:',
    example: 'NARRATOR (VO)',
    after: ['At any time, use the Element Menu to switch between Scene Heading, Action, Character, Dialogue, Parenthetical, Transition and other screenplay elements.'],
    icon: FiMic,
  },
  {
    title: 'Your AI Creative Assistant',
    body: [
      'Morphosys AI is designed to support your creative process. Use the AI Workspace on your Dashboard to explore ideas, develop characters, refine dialogue and strengthen your storytelling. You remain the writer and make every creative decision.',
      'Morphosys AI assists writers; it does not replace them or independently write complete scripts.',
    ],
    icon: FiCpu,
  },
  {
    title: 'Enhance Your Work with AI',
    body: [
      'Use AI tools to assist with your existing screenplay, explore creative possibilities and develop visual storyboards. AI Video may also be available depending on your account access. Your original writing remains yours.',
    ],
    icon: FiZap,
  },
  {
    title: 'Understanding AI Credits',
    body: [
      'Each AI tool uses a specific number of credits. You can see the credit cost before using a tool and check your available balance in the AI Workspace on your Dashboard.',
    ],
    icon: FiStar,
  },
  {
    title: 'You’re Ready to Write',
    body: ['Press Enter to move through the standard screenplay workflow while Morphosys automatically handles the formatting, allowing you to focus on telling your story.'],
    final: true,
    icon: FiCheck,
  },
];

// Overlay shown on top of the real editor (no simulated background).
const OnboardingOverlay = ({ onDone }) => {
  const [i, setI] = useState(0);
  const card = cards[i];
  const CardIcon = card.icon;

  const finish = () => {
    markOnboardingCompleted();
    onDone && onDone();
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/30 overflow-auto text-left flex items-center justify-center p-3 sm:p-4">
      <style>{`
        @keyframes morphosys-card-enter {
          from { opacity: 0; transform: translateY(22px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .morphosys-card-enter { animation: morphosys-card-enter 320ms cubic-bezier(0.22, 1, 0.36, 1); }
        @media (prefers-reduced-motion: reduce) {
          .morphosys-card-enter { animation: none; }
        }
      `}</style>
      <div
        key={i}
        role="dialog"
        aria-label={card.title}
        className="morphosys-card-enter w-full max-w-[420px] max-h-[calc(100svh-24px)] overflow-y-auto bg-white rounded-2xl shadow-xl border border-gray-100 p-5 sm:p-6"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#EAF6FF] text-[#0099FF]">
              <CardIcon aria-hidden="true" size={22} strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#768394] mb-1">{i + 1} of {cards.length}</p>
              <h2 className="text-lg sm:text-xl leading-tight font-bold text-[#252C34]">{card.title}</h2>
            </div>
          </div>
          <div className="flex shrink-0 gap-1 pt-1" aria-hidden="true">
            {cards.map((_, index) => (
              <span key={index} className={`h-1.5 rounded-full transition-all duration-300 ${index === i ? 'w-4 bg-[#0099FF]' : 'w-1.5 bg-gray-200'}`} />
            ))}
          </div>
        </div>
        <div className="h-px bg-gray-100 mb-4" />
        <div className="space-y-2.5 text-sm sm:text-[15px] leading-relaxed text-[#404040]">
          {card.body.map((t, k) => <p key={k}>{t}</p>)}
          {card.list && (
            <ul className="list-disc pl-5 space-y-1">{card.list.map((t) => <li key={t}>{t}</li>)}</ul>
          )}
          {card.exampleLabel && <p className="font-medium">{card.exampleLabel}</p>}
          {card.example && (
            <p className="font-mono bg-[#F7FBFE] border border-[#D7ECFA] rounded-lg px-3 py-2.5 text-[#252C34]">{card.example}</p>
          )}
          {card.after?.map((t, k) => <p key={k}>{t}</p>)}
        </div>

        {card.final ? (
          <div className="mt-5 flex flex-col gap-2.5">
            <button onClick={finish} className="w-full rounded-full text-white font-bold bg-[#0099FF] hover:bg-[#008AE6] transition-colors py-2.5 text-base">
              Start Writing
            </button>
            <div className="flex justify-between items-center">
              <button onClick={() => setI(i - 1)} className="text-sm font-medium text-[#768394] hover:text-[#404040] transition-colors">Back</button>
              <button onClick={finish} className="text-sm font-medium text-[#768394] hover:text-[#404040] transition-colors">Skip</button>
            </div>
          </div>
        ) : (
          <div className="mt-5 flex justify-between items-center">
            <button
              onClick={() => setI(i - 1)}
              disabled={i === 0}
              className="px-4 py-2 rounded-full border border-gray-300 text-sm font-semibold text-[#404040] hover:bg-gray-50 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
            >
              Back
            </button>
            <button onClick={finish} className="text-sm font-medium text-[#768394] hover:text-[#404040] transition-colors">Skip</button>
            <button onClick={() => setI(i + 1)} className="px-6 py-2 rounded-full bg-[#0099FF] hover:bg-[#008AE6] transition-colors text-white text-sm font-bold">
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingOverlay;
